import { lazy } from 'react';
import { Navigate } from 'react-router';

// project import
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import PagesLayout from 'layout/Pages';

import ProtectedComponent from './ProtectedComponent';
import { isProduction } from 'utils/helper';
import PharmacyInvoicePage from 'pages/invoice/pharmacyInvoice';
import InvoiceListPage from 'pages/invoice/invoicelist';
import SaleInvoice from 'pages/inventory/SaleBill';
import SaleBillList from 'pages/inventory/SaleBillList';

// render - my account (change password)
const AccountProfile = Loadable(lazy(() => import('pages/apps/profiles/account')));
const AccountTabPassword = Loadable(lazy(() => import('sections/apps/profiles/account/TabPassword')));

// pages routing
const AuthLogin = Loadable(lazy(() => import('pages/auth/jwt/login')));
const AuthRegister = Loadable(lazy(() => import('pages/auth/jwt/register')));
const AuthForgotPassword = Loadable(lazy(() => import('pages/auth/jwt/forgot-password')));
const AuthResetPassword = Loadable(lazy(() => import('pages/auth/jwt/reset-password')));
const AuthCheckMail = Loadable(lazy(() => import('pages/auth/jwt/check-mail')));
const AuthCodeVerification = Loadable(lazy(() => import('pages/auth/jwt/code-verification')));

const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));
const MaintenanceUnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction')));
const MaintenanceComingSoon = Loadable(lazy(() => import('pages/maintenance/coming-soon')));
const OPDCheckup = Loadable(lazy(() => import('pages/admit/OPDCheckup')));
const UserDetail = Loadable(lazy(() => import('pages/admin/userDetail')));
const PrescriptionHistory = Loadable(lazy(() => import('pages/admit/PrescriptionHistory')));
const PrescriptionTemplates = Loadable(lazy(() => import('pages/admit/PrescriptionTemplates')));
const EditPrescriptionTemplate = Loadable(lazy(() => import('pages/admit/EditPrescriptionTemplate')));
const SuperUserPreferences = Loadable(lazy(() => import('pages/admin/SuperUserPreferences')));
const AuditLogList = Loadable(lazy(() => import('pages/admin/AuditLogList')));
const WhatsappConversations = Loadable(lazy(() => import('pages/admin/WhatsappConversations')));
const DailyCashCollection = Loadable(lazy(() => import('pages/account/DailyCashCollection')));

const TotalCollection = Loadable(lazy(() => import('pages/account/TotalCollection')));
const CancellationLedger = Loadable(lazy(() => import('pages/account/CancellationLedger')));
const PatientWalletManagement = Loadable(lazy(() => import('pages/account/PatientWalletManagement')));
const PaymentRequests = Loadable(lazy(() => import('pages/payments/PaymentRequests')));


// render - sample page

// render - reception page
const ReceptionList = Loadable(lazy(() => import('pages/reception/patientList')));

// render - doctor page
const AppointmentList = Loadable(lazy(() => import('pages/doctor/appointmentList')));

// render - users page
const UserList = Loadable(lazy(() => import('pages/admin/userList')));
const MedicineList = Loadable(lazy(() => import('pages/admin/medicineList')));
const SaltInteractions = Loadable(lazy(() => import('pages/admin/SaltInteractions')));
const LabTestList = Loadable(lazy(() => import('pages/admin/labTestList')));
const LabManagement = Loadable(lazy(() => import('pages/lab/labManagement')));
const RadiologyManagement = Loadable(lazy(() => import('pages/lab/radiologyManagement')));
const RadiologyDicomViewer = Loadable(lazy(() => import('pages/lab/RadiologyDicomViewer')));
const RadiologyReportWriter = Loadable(lazy(() => import('pages/lab/RadiologyReportWriter')));
const RadiologyTemplates = Loadable(lazy(() => import('pages/lab/RadiologyTemplates')));
const EditRadiologyTemplate = Loadable(lazy(() => import('pages/lab/EditRadiologyTemplate')));
const AppointmentStatusVerification = Loadable(lazy(() => import('pages/admin/AppointmentStatusVerification')));
const Dashboard = Loadable(lazy(() => import('pages/admin/dashboard')));
const ExportData = Loadable(lazy(() => import('pages/admin/exportData')));
const InboundCrmAttempts = Loadable(lazy(() => import('pages/admin/InboundCrmAttempts')));
const NonMedicineItemsList = Loadable(lazy(() => import('pages/admin/nonMedicineItems')));

// render - inventory page
const Inventory = Loadable(lazy(() => import('pages/inventory/Inventory')));
const AddBill = Loadable(lazy(() => import('pages/inventory/add-bill/AddBill')));
const BillList = Loadable(lazy(() => import('pages/inventory/BillList')));
const Stores = Loadable(lazy(() => import('pages/inventory/Stores')));
const StoreTransfers = Loadable(lazy(() => import('pages/inventory/StoreTransfers')));

const LabInvoice = Loadable(lazy(() => import('pages/invoice/labInvoice')));

// render - vaccination
const VaccinationManagement = Loadable(lazy(() => import('pages/vaccination/VaccinationManagement')));
const VaccineManagement = Loadable(lazy(() => import('pages/vaccination/VaccineManagement')));

// render - IPD pages
const IPDashboard = Loadable(lazy(() => import('pages/ipd/IPDDashboard')));
const PatientAdmission = Loadable(lazy(() => import('pages/ipd/PatientAdmission')));
const DischargeManagement = Loadable(lazy(() => import('pages/ipd/DischargeManagement')));
const FloorManagement = Loadable(lazy(() => import('pages/ipd/FloorManagement')));
const RoomManagement = Loadable(lazy(() => import('pages/ipd/RoomManagement')));
const BedManagement = Loadable(lazy(() => import('pages/ipd/BedManagement')));
const BedReservation = Loadable(lazy(() => import('pages/ipd/BedReservation')));
const IPDReports = Loadable(lazy(() => import('pages/ipd/IPDReports')));
const IPDReportWriter = Loadable(lazy(() => import('pages/ipd/IPDReportWriter')));


// render - lab report
const LabReport = Loadable(lazy(() => import('pages/lab/LabReport')));

// render - insurance pages
const InsurerList = Loadable(lazy(() => import('pages/insurance/insurerList')));
const InsuranceRegistration = Loadable(lazy(() => import('pages/insurance/insuranceRegistration')));
const InsurancePricing = Loadable(lazy(() => import('pages/insurance/insurancePricing')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        {
          path: 'user-list', //TODO:rename as users
          // element: <ProtectedComponent routeId="users"><UserList /></ProtectedComponent>,
          children: [
            {
              index: true,
              element: (
                <ProtectedComponent routeId="users">
                  <UserList />
                </ProtectedComponent>
              ),
            },
            {
              path: 'details',
              element: (
                <ProtectedComponent routeId="users">
                  <UserDetail />
                </ProtectedComponent>
              )
            },
          ],
        },
        {
          path: 'medicine-list',
          element: <ProtectedComponent routeId="medicines"><MedicineList /></ProtectedComponent>
        },
        {
          path: 'salt-interactions',
          element: <ProtectedComponent routeId="medicines"><SaltInteractions /></ProtectedComponent>
        },
        {
          path: 'non-medicine-list',
          element: <ProtectedComponent routeId="medicines"><NonMedicineItemsList /></ProtectedComponent>
        },
        {
          path: 'insurance',
          children: [
            {
              path: 'providers',
              element: <ProtectedComponent routeId="insurance"><InsurerList /></ProtectedComponent>
            },
            {
              path: 'registration',
              element: <ProtectedComponent routeId="insurance"><InsuranceRegistration /></ProtectedComponent>
            },
            {
              path: 'pricing',
              element: <ProtectedComponent routeId="insurance"><InsurancePricing /></ProtectedComponent>
            },
          ]
        },
        {
          path: 'admin',
          children: [
            {
              path: 'dashboard',
              element: <ProtectedComponent routeId="admin"><Dashboard /></ProtectedComponent>
            },
            {
              path: 'appointment-status-verification',
              element: <ProtectedComponent routeId="admin"><AppointmentStatusVerification /></ProtectedComponent>
            },
            {
              path: 'export-data',
              element: <ProtectedComponent routeId="admin"><ExportData /></ProtectedComponent>
            },
            {
              path: 'super-user-preferences',
              element: <ProtectedComponent routeId="admin"><SuperUserPreferences /></ProtectedComponent>
            },
            {
              path: 'inbound-crm',
              element: <ProtectedComponent routeId="crm"><InboundCrmAttempts /></ProtectedComponent>
            },
            {
              path: 'audit-logs',
              element: <ProtectedComponent routeId="admin"><AuditLogList /></ProtectedComponent>
            },
            {
              path: 'whatsapp-conversations',
              element: <ProtectedComponent routeId="crm"><WhatsappConversations /></ProtectedComponent>
            }
          ]
        },
        {
          path: 'invoice',
          children: [
            {
              path: 'list',
              element: <InvoiceListPage />
            },
            {
              path: 'lab-invoice',
              element: <LabInvoice />
            },
            {
              path: 'pharmacy-invoice',
              element: <PharmacyInvoicePage />
            },
            {
              path: 'bulk-cancel-lab-invoice',
              element: <LabInvoice variant="lab_bulk_cancel" />
            },
            {
              path: 'bulk-cancel-pharmacy-invoice',
              element: <PharmacyInvoicePage variant="pharmacy_bulk_cancel" />
            }
          ]
        },
        {
          path: 'dashboard',
          children: [
            {
              path: 'dashboard',
              element: <Dashboard />
            },
          ]
        },
        {
          path: 'apps/profiles/account',
          element: <AccountProfile />,
          children: [
            {
              path: 'password',
              element: <AccountTabPassword />
            }
          ]
        },
        {
          path: 'reception',
          children: [
            {
              path: 'patient-list',
              element: <ProtectedComponent routeId="reception"><ReceptionList /></ProtectedComponent>
            },
          ]
        },
        {
          path: 'appointments',
          children: [
            {
              path: 'appointment-list',
              element: <AppointmentList />
            },
          ]
        },
        {
          path: 'labs',
          children: [
            {
              path: 'lab-test-list',
              element: <ProtectedComponent routeId="labs/lab-test-list"><LabTestList /></ProtectedComponent>
            },
            {
              path: 'lab-management',
              element: <ProtectedComponent routeId="labs/lab-management"><LabManagement /></ProtectedComponent>
            },
            {
              path: 'radiology-management',
              element: <RadiologyManagement />
            },
            {
              path: 'report/:id',
              element: <ProtectedComponent routeId="labs/report"><LabReport /></ProtectedComponent>
            },
            {
              path: 'view-dicom',
              element: <RadiologyDicomViewer />
            },
            {
              path: 'write-report/:id?',
              element: <RadiologyReportWriter />
            },
            {
              path: 'radiology-templates',
              children: [
                {
                  index: true,
                  element: <RadiologyTemplates />
                },
                {
                  path: 'edit/:id',
                  element: <EditRadiologyTemplate />
                }
              ]
            }
          ]
        },
        {
          path: 'patients',
          children: [
            {
              path: 'admit',
              element: <OPDCheckup />
            },
            {
              path: 'past-details',
              element: <PrescriptionHistory />
            }
          ]
        },
        {
          path: 'prescription-templates',
          children: [
            {
              index: true,
              element: <PrescriptionTemplates />
            },
            {
              path: 'edit/:id',
              element: <EditPrescriptionTemplate />
            }
          ]
        },
        {
          path: 'account',
          children: [
            {
              path: 'payment-requests',
              element: <ProtectedComponent routeId="account"><PaymentRequests /></ProtectedComponent>
            },
            {
              path: 'daily-cash-collection',
              element: <ProtectedComponent routeId="account"><DailyCashCollection /></ProtectedComponent>
            },
            {
              path: 'total-collection',
              element: <ProtectedComponent routeId="account"><TotalCollection /></ProtectedComponent>
            },
            {
              path: 'cancellation-ledger',
              element: <ProtectedComponent routeId="account"><CancellationLedger /></ProtectedComponent>
            },
            {
              path: 'patient-wallet',
              element: (
                <ProtectedComponent routeId="account" allowedRoles={['receptionist', 'account_manager']}>
                  <PatientWalletManagement />
                </ProtectedComponent>
              )
            }
          ]
        },
        {
          path: 'inventory',
          children: [
            {
              path: 'inventory-list',
              element: <Inventory />
            },
            {
              path: 'bills',
              children: [
                {
                  path: '',
                  element: <BillList />,
                },
                {
                  path: 'add-bill',
                  element: <AddBill />
                }
              ]
            },
            {
              path: 'stores',
              element: <Stores />
            },
            {
              path: 'store-transfers',
              element: <StoreTransfers />
            },
            {
              path: 'sale-bills',
              children: [
                {
                  path: '',
                  element: <ProtectedComponent routeId="sale-bills"><SaleBillList /></ProtectedComponent>
                },
                {
                  path: 'add-bill',
                  element: <ProtectedComponent routeId="sale-bills"><SaleInvoice /></ProtectedComponent>
                },
              ]
            }
          ]
        },
        {
          path: 'ipd',
          children: [
            {
              path: '',
              element: <ProtectedComponent routeId="ipd/dashboard"><IPDashboard /></ProtectedComponent>
            },
            {
              path: 'dashboard',
              element: <ProtectedComponent routeId="ipd/dashboard"><IPDashboard /></ProtectedComponent>
            },
            {
              path: 'admission',
              element: <ProtectedComponent routeId="ipd/admission"><PatientAdmission /></ProtectedComponent>
            },
            {
              path: 'discharge',
              element: <ProtectedComponent routeId="ipd/discharge"><DischargeManagement /></ProtectedComponent>
            },
            {
              path: 'print-forms',
              element: <ProtectedComponent routeId="ipd/report-writer"><IPDReportWriter /></ProtectedComponent>
            },
            {
              path: 'report-writer',
              element: <Navigate to="/ipd/print-forms" replace />
            },
            {
              path: 'floors',
              element: <ProtectedComponent routeId="ipd/floors"><FloorManagement /></ProtectedComponent>
            },
            {
              path: 'rooms',
              element: <ProtectedComponent routeId="ipd/rooms"><RoomManagement /></ProtectedComponent>
            },
            {
              path: 'beds',
              element: <ProtectedComponent routeId="ipd/beds"><BedManagement /></ProtectedComponent>
            },
            {
              path: 'reservations',
              element: <ProtectedComponent routeId="ipd/reservations"><BedReservation /></ProtectedComponent>
            },
            {
              path: 'reports',
              element: (
                <ProtectedComponent routeId="ipd/reports">
                  {isProduction ? <Navigate to="/ipd" replace /> : <IPDReports />}
                </ProtectedComponent>
              )
            }
          ]
        },
        {
          path: 'vaccination',
          children: [
            {
              path: 'management',
              element: (
                <ProtectedComponent routeId="vaccination">
                  <VaccinationManagement />
                </ProtectedComponent>
              )
            },
            {
              path: 'vaccine-management',
              element: (
                <ProtectedComponent routeId="vaccination">
                  <VaccineManagement />
                </ProtectedComponent>
              )
            }
          ]
        },
      ]
    },
    {
      path: '/maintenance',
      element: <PagesLayout />,
      children: [
        {
          path: '404',
          element: <MaintenanceError />
        },
        {
          path: '500',
          element: <MaintenanceError500 />
        },
        {
          path: 'under-construction',
          element: <MaintenanceUnderConstruction />
        },
        {
          path: 'coming-soon',
          element: <MaintenanceComingSoon />
        }
      ]
    },
    {
      path: '/auth',
      element: <PagesLayout />,
      children: [
        {
          path: 'login',
          element: <AuthLogin />
        },
        {
          path: 'register',
          element: <AuthRegister />
        },
        {
          path: 'forgot-password',
          element: <AuthForgotPassword />
        },
        {
          path: 'reset-password',
          element: <AuthResetPassword />
        },
        {
          path: 'check-mail',
          element: <AuthCheckMail />
        },
        {
          path: 'code-verification',
          element: <AuthCodeVerification />
        }
      ]
    },
  ]
};

export default MainRoutes;
